import React from 'react';

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* SEO Meta Tags (would be in index.html head) */}
      <div className="hidden">
        {/* This represents what would go in your HTML head tag */}
        <title>PopMart Auto-Checker - Never Miss Limited Edition Collectibles Again</title>
        <meta name="description" content="Automatically monitor PopMart store pickup and delivery availability. Get instant notifications when your favorite collectibles are back in stock." />
        <meta name="keywords" content="PopMart, Labubu, bot, Chrome extension, blind boxes, script, store pickup, auto checker" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="PopMart Auto-Checker - Never Miss Limited Edition Collectibles" />
        <meta property="og:description" content="Get instant notifications when PopMart collectibles are back in stock. Supports both store pickup and delivery options." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://your-domain.com" />
      </div>

      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">PM</span>
              </div>
              <h1 className="text-xl font-bold text-gray-900">PopMart Auto-Checker</h1>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#features" className="text-gray-600 hover:text-gray-900 transition-colors">Features</a>
              <a href="#how-it-works" className="text-gray-600 hover:text-gray-900 transition-colors">How It Works</a>
              <a href="#install" className="text-gray-600 hover:text-gray-900 transition-colors">Install</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Never Miss Your Favorite
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent block">
                PopMart Collectibles
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Automatically monitor PopMart store pickup and delivery availability. Get instant notifications when Labubu, blind boxes, and Skull Panda are back in stock.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <a 
                href="https://chromewebstore.google.com/detail/ofefgemfldjmeiccdkepddjkdhndkoea?utm_source=item-share-cb" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center space-x-2"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <span>Add to Chrome</span>
              </a>
              <div className="text-sm text-gray-500 flex items-center space-x-1">
                <span>⭐⭐⭐⭐⭐</span>
                <span>Rated 5/5 by collectors</span>
              </div>
            </div>
            <p className="text-sm text-gray-500">✨ No signup required • Works instantly</p>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Tired of Missing Out on Limited Edition Drops?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              PopMart collectibles sell out fast. Manual checking wastes time and you still miss restocks.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">😤</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Constant Refreshing</h3>
              <p className="text-gray-600">Spending hours manually checking if your favorite figures are back in stock</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">⏰</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Missing Restocks</h3>
              <p className="text-gray-600">Limited editions restock and sell out again before you even know about it</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💸</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Paying Reseller Prices</h3>
              <p className="text-gray-600">Forced to buy from resellers at 2x-3x the original retail price</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              The Smartest Way to Monitor PopMart Stock
            </h2>
            <p className="text-lg text-gray-600">
              Automated monitoring that actually works. Get notified the instant your items are available.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-sm border hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-xl">🔔</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Instant Notifications</h3>
              <p className="text-gray-600">Get notified the second your tracked items become available for purchase</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm border hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-xl">🛒</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Auto Add to Cart</h3>
              <p className="text-gray-600">Automatically adds items to your cart and starts checkout process</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm border hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-xl">🏪</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Store Pickup & Delivery</h3>
              <p className="text-gray-600">Monitor both store pickup availability and online delivery options</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm border hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-xl">⚡</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Lightning Fast</h3>
              <p className="text-gray-600">Checks multiple items simultaneously with customizable timing intervals</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm border hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-xl">🎯</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Multi-Item Tracking</h3>
              <p className="text-gray-600">Track unlimited PopMart items across all collections and series</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm border hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <span className="text-xl">🔒</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">100% Safe & Secure</h3>
              <p className="text-gray-600">No personal data collected. Works entirely in your browser</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How PopMart Auto-Checker Works
            </h2>
            <p className="text-lg text-gray-600">Get started in under 2 minutes</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-white font-bold">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-4">Install Extension</h3>
              <p className="text-gray-600 mb-4">Add PopMart Auto-Checker to Chrome from the Web Store</p>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <code className="text-sm text-blue-600">https://chromewebstore.google.com/</code>
              </div>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-white font-bold">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-4">Add Item IDs</h3>
              <p className="text-gray-600 mb-4">Enter the PopMart item IDs you want to track. Choose between store pickup or delivery monitoring.</p>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <code className="text-sm text-gray-600">Item ID: 1757, 1876, 1875...</code>
              </div>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-pink-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-white font-bold">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-4">Get Notified</h3>
              <p className="text-gray-600 mb-4">Relax while the extension monitors stock. Get instant notifications and auto-checkout when items are available.</p>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <span className="text-sm text-green-600">✅ Item found & added to cart!</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Loved by PopMart Collectors Worldwide
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-sm border">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold mr-3">
                  S
                </div>
                <div>
                  <h4 className="font-semibold">Sarah M.</h4>
                  <div className="text-yellow-400">⭐⭐⭐⭐⭐</div>
                </div>
              </div>
              <p className="text-gray-600">"Finally got the Molly figure I've been hunting for months! This extension is a game-changer for collectors."</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm border">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-3">
                  M
                </div>
                <div>
                  <h4 className="font-semibold">Marcus L.</h4>
                  <div className="text-yellow-400">⭐⭐⭐⭐⭐</div>
                </div>
              </div>
              <p className="text-gray-600">"Saved me hundreds of dollars by catching restocks at retail price instead of paying resellers. Worth every penny!"</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm border">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white font-bold mr-3">
                  J
                </div>
                <div>
                  <h4 className="font-semibold">Jessica K.</h4>
                  <div className="text-yellow-400">⭐⭐⭐⭐⭐</div>
                </div>
              </div>
              <p className="text-gray-600">"The auto-checkout feature is incredible. By the time I get the notification, it's already in my cart!"</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="install" className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Start Catching Every PopMart Restock Today
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of collectors who never miss limited edition drops
          </p>
          <a 
            href="https://chromewebstore.google.com/detail/ofefgemfldjmeiccdkepddjkdhndkoea?utm_source=item-share-cb" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transform hover:scale-105 transition-all duration-200 space-x-2"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            <span>Install PopMart Auto-Checker</span>
          </a>
          <p className="text-blue-100 text-sm mt-4">✨ 30-second setup • Works immediately</p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          
          <div className="space-y-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold mb-2">How do I find PopMart item IDs?</h3>
              <p className="text-gray-600">Item IDs are found in the PopMart product URL. For example, if the URL is popmart.com/us/products/1757, then 1757 is the item ID.</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold mb-2">Does this work with store pickup and delivery?</h3>
              <p className="text-gray-600">Yes! You can choose to monitor either store pickup availability or delivery options. The extension works with both shopping methods.</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold mb-2">Is my personal information safe?</h3>
              <p className="text-gray-600">Absolutely. The extension only stores your item preferences locally in your browser. No personal data is collected or transmitted to external servers.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">PM</span>
              </div>
              <span className="text-xl font-bold">PopMart Auto-Checker</span>
            </div>
            <p className="text-gray-400 mb-6">
              The #1 Chrome extension for PopMart stock monitoring
            </p>
            <div className="flex justify-center space-x-6 mb-8">
              <a href="https://chromewebstore.google.com/detail/ofefgemfldjmeiccdkepddjkdhndkoea?utm_source=item-share-cb" className="text-gray-400 hover:text-white transition-colors">
                Chrome Store
              </a>
              <a href="https://sites.google.com/view/popmart-store-ext-privacy/home" className="text-gray-400 hover:text-white transition-colors">
                Privacy Policy
              </a>
            </div>
            <p className="text-gray-500 text-sm">
              © 2025 PopMart Auto-Checker. Not affiliated with PopMart International.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;